/**
 * Admin Panel - Plugins Management API
 *
 * Zod schemas for plugin stats and health check endpoints.
 * Part of the NexusApi system for end-to-end type safety.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Schemas
// ============================================================

/**
 * Plugin statistics response schema
 * Comprehensive stats by type, status, privacy, and categories
 */
export const PluginStatsSchema = z.object({
  total: z.number(),
  byType: z.object({
    pipedream: z.number(),
    apify: z.number(),
    custom: z.number()
  }),
  byStatus: z.object({
    published: z.number(),
    draft: z.number(),
    archived: z.number()
  }),
  byPrivacy: z.object({
    public: z.number(),
    private: z.number()
  }),
  categories: z.object({
    withCategories: z.number(),
    withoutCategories: z.number(),
    topCategories: z.array(
      z.object({
        name: z.string(),
        count: z.number()
      })
    )
  }),
  timestamp: z.string()
});

/**
 * Plugin health check issue schema
 */
export const PluginHealthIssueSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  issue: z.string(),
  severity: z.enum(["warning", "error"])
});

/**
 * Plugin health check response schema.
 *
 * ## `summary.total` is gone
 *
 * It was `issues.length`, so it carried nothing the client did not already hold
 * — and it was worse than redundant. `issues` is emitted by a loop that pushes
 * up to THREE entries per tool, over a `take: limit` page of tools. So
 * `summary.total` multiplied two different populations together and published
 * the product under the word "total", which the UI rendered as "Total Issues".
 *
 * `warnings` and `errors` stay. They are exact severity counts OF `issues`,
 * they claim nothing about the estate, and `truncated` now says whether the
 * estate was fully examined.
 */
export const PluginHealthResponseSchema = z.object({
  issues: z.array(PluginHealthIssueSchema),
  truncated: z
    .boolean()
    .describe(
      "The tool query hit its `limit` before running out of tools with issues, so tools beyond this page were never examined and further issues may exist."
    ),
  summary: z.object({
    warnings: z.number().describe("Issues in `issues` with severity 'warning'"),
    errors: z.number().describe("Issues in `issues` with severity 'error'")
  })
});

/**
 * Query params for GetHealth endpoint
 */
export const PluginHealthParamsSchema = z.object({
  limit: z.number().optional()
});

/**
 * Archive deprecated plugins params
 */
export const ArchiveDeprecatedParamsSchema = z.object({
  dryRun: z.boolean().optional()
});

/**
 * Archive deprecated plugins response.
 *
 * `dryRun` is REQUIRED, not optional. It was optional and no return path set
 * it, so the audit interceptor's `extractMeta` — which branches on it — read
 * undefined every time and recorded a dry run as a completed archive. A field
 * an auditor branches on cannot be one the handler may omit.
 *
 * `tools` carries objects, not strings: the handler has always returned
 * `{ name, type }` and the `z.array(z.string())` here never matched it. Nothing
 * caught that because the handler was unannotated.
 */
export const ArchiveDeprecatedResponseSchema = z.object({
  status: z.enum(["dry_run", "skipped", "success"]),
  message: z.string(),
  count: z.number(),
  dryRun: z.boolean(),
  /// Sample of what was (or would be) archived, capped at 10 by the handler.
  /// `name` is `publicName ?? privateName`, both of which are nullable columns.
  tools: z
    .array(
      z.object({
        name: z.string().nullable(),
        type: z.string()
      })
    )
    .optional()
});

/**
 * Delete plugin path vars
 */
export const DeletePluginPathVarsSchema = z.object({
  toolId: z.string().uuid()
});

/**
 * Delete plugin response.
 *
 * `status` is a literal and `tool` is required: a missing plugin is now a 404,
 * so "success" is the only outcome a 2xx can carry. It used to answer HTTP 200
 * with `status: "error"` and no `tool`, which the admin agent — the only caller
 * of this route — reads as a plugin it deleted.
 *
 * The field is `name` (`publicName ?? privateName`), which is what the handler
 * has always returned; the `publicName` declared here matched nothing.
 */
export const DeletePluginResponseSchema = z.object({
  status: z.literal("success"),
  message: z.string(),
  tool: z.object({
    id: z.string(),
    name: z.string().nullable(),
    type: z.string()
  })
});

// ============================================================
// ZAdminPlugins - Endpoint Definitions
// ============================================================

export const ZAdminPlugins = {
  /**
   * Get comprehensive plugin statistics
   * GET /api/admin/plugins/stats
   */
  GetStats: {
    method: "GET" as const,
    path: "/api/admin/plugins/stats",
    Response: ApiSuccessSchema(PluginStatsSchema)
  },

  /**
   * Get plugin health check results
   * GET /api/admin/plugins/health?limit=100
   */
  GetHealth: {
    method: "GET" as const,
    path: "/api/admin/plugins/health",
    Params: PluginHealthParamsSchema,
    Response: ApiSuccessSchema(PluginHealthResponseSchema)
  },

  /**
   * Archive deprecated plugins (Apify)
   * POST /api/admin/plugins/archive-deprecated?dryRun=true
   */
  ArchiveDeprecated: {
    method: "POST" as const,
    path: "/api/admin/plugins/archive-deprecated",
    Params: ArchiveDeprecatedParamsSchema,
    Response: ApiSuccessSchema(ArchiveDeprecatedResponseSchema)
  },

  /**
   * Delete a specific plugin
   * DELETE /api/admin/plugins/:toolId
   */
  DeletePlugin: {
    method: "DELETE" as const,
    path: "/api/admin/plugins/:toolId",
    PathVars: DeletePluginPathVarsSchema,
    Response: ApiSuccessSchema(DeletePluginResponseSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type PluginStats = z.infer<typeof PluginStatsSchema>;
export type PluginHealthIssue = z.infer<typeof PluginHealthIssueSchema>;
export type PluginHealthResponse = z.infer<typeof PluginHealthResponseSchema>;
